import InvestmentStatusBadge from './InvestmentStatusBadge';
import { MobileCard, DataRow } from '../ui/MobileCard';
import { formatCurrency } from '../../lib/formatPlan';

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * Shared investment list rendering — used by both the global admin
 * Investments list and the embedded per-investor list on Investor Detail.
 * `getPlan(uuid)` resolves a plan name via usePlanLookup; `showInvestorColumn`
 * is only true on the global list, since a per-investor list would be
 * redundant. The investment response has no investor info inline (only
 * user_uuid) — showing a truncated uuid here is a known display gap, not
 * an invented name, per the integration spec's explicit instruction.
 */
export default function InvestmentTable({ investments, getPlan, onRowClick, showInvestorColumn }) {
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-foreground-subtle border-b border-border">
              {showInvestorColumn && <th className="py-2 pr-4 font-medium">Investor</th>}
              <th className="py-2 pr-4 font-medium">Plan</th>
              <th className="py-2 pr-4 font-medium">Principal</th>
              <th className="py-2 pr-4 font-medium">Funded / Remaining</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {investments.map((inv) => {
              const plan = getPlan?.(inv.investmentPlanUuid);
              return (
                <tr
                  key={inv.id}
                  className="cursor-pointer hover:bg-background-elevated transition-colors"
                  onClick={() => onRowClick(inv)}
                >
                  {showInvestorColumn && (
                    <td className="py-3 pr-4 text-foreground-muted" title={inv.userUuid || ''}>
                      {inv.userUuid ? `${inv.userUuid.slice(0, 8)}…` : '—'}
                    </td>
                  )}
                  <td className="py-3 pr-4 text-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      {inv.payoutSummary?.hasPendingPayout && (
                        <span className="h-2 w-2 rounded-full bg-warning shrink-0" title="Payout pending — behind schedule" />
                      )}
                      {plan?.name || 'Unknown plan'}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-foreground-muted">{formatCurrency(inv.principal)}</td>
                  <td className="py-3 pr-4 text-foreground-muted">
                    {formatCurrency(inv.fundedAmount)} / {formatCurrency(inv.remainingAmount)}
                  </td>
                  <td className="py-3 pr-4">
                    <InvestmentStatusBadge status={inv.status} />
                  </td>
                  <td className="py-3 text-foreground-muted">{formatDate(inv.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3">
        {investments.map((inv) => {
          const plan = getPlan?.(inv.investmentPlanUuid);
          return (
            <MobileCard key={inv.id} className="cursor-pointer" onClick={() => onRowClick(inv)}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-foreground inline-flex items-center gap-1.5">
                  {inv.payoutSummary?.hasPendingPayout && (
                    <span className="h-2 w-2 rounded-full bg-warning shrink-0" title="Payout pending — behind schedule" />
                  )}
                  {plan?.name || 'Unknown plan'}
                </p>
                <InvestmentStatusBadge status={inv.status} />
              </div>
              {showInvestorColumn && inv.userUuid && <DataRow label="Investor" value={`${inv.userUuid.slice(0, 8)}…`} />}
              <DataRow label="Principal" value={formatCurrency(inv.principal)} />
              <DataRow label="Funded" value={formatCurrency(inv.fundedAmount)} />
              <DataRow label="Created" value={formatDate(inv.createdAt)} />
            </MobileCard>
          );
        })}
      </div>
    </>
  );
}
